import Clock from '@/components/ActionRow/Clock';
import EllipsisTooltipText from '@/components/ActionRow/EllipsisTooltipText';
import DeleteActionModal from '@/components/DeleteActionModal';
import DuplicateActionModal from '@/components/DuplicateActionModal';
import IconProgressState from '@/components/IconProgressState';
import LeverageRequestModal from '@/components/LeverageRequestModal';
import PersonActionPreview from '@/components/PersonActionPreview';
import PreferredButton from '@/components/PreferredButton';
import RPMDateTimePicker from '@/components/RPMDateTimePicker/RPMDateTimePicker';
import CancelRecurringActionModal from '@/components/CancelRecurringActionModal';
import RecurringConfirmationModal from '@/components/RecurringEventModal/ConfirmationModal';
import RecurringStatusBlockedModal from '@/components/RecurringEventModal/RecurringStatusBlockedModal';
import ScheduleButton from '@/components/ScheduleButton';
import ThreeDotsActionButton from '@/components/ThreeDotsActionButton';
import { ActionRelation, COMPLETE } from '@/constants/action';
import { DAY, WEEK } from '@/constants/calendar';
import { useSoundEffects } from '@/contexts/SoundEffects';
import { TOGGLE_BLOCK_COMPLETE } from '@/gql/block';
import { UpdateBlockResponse } from '@/gql/block/types';
import { keys } from '@/gql/global/keys';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import { useDeleteAction, useUpdateAction, useUpdateActionStatus } from '@/services/action/hooks';
import { useUpdateEventByPk } from '@/services/events/hooks';
import { fetchData } from '@/services/graphql';
import { onMutateBlockStatus, useCompleteBlockStatus } from '@/services/myPlan/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconProject, IconRecurrence } from '@/theme/icons';
import { Action, ProgressStatus, RecurringWeekContext } from '@/types/actions';
import { DayWeek } from '@/types/calendar';
import { refetchActions } from '@/utils/action';
import {
  capRecurringOccurrencesForDisplay,
  endOfTheWeek,
  formatDateToString,
  getRecurringCompletedInstanceStartsInPeriod,
  getRecurringCompletedSeriesStats,
  getRecurringOccurrenceTooltipInfo,
  getRecurringPeriodViewModel,
  getRecurringSeriesLastCompletedOccurrenceStart,
  mergeRecurrenceCompletingAllInstancesInRange,
  startOfTheWeek,
} from '@/utils/calendar';
import { generateLinearGradient } from '@/utils/color';
import { validateRecurrence } from '@/utils/events';
import { capitalizeFirstLetter } from '@/utils/index';
import rem from '@/utils/rem';
import { invalidateQueries, refetchQueries } from '@/utils/tanStackQuery';
import {
  trackActionMovedDuringComplete,
  trackActionProgress,
  trackActionStarred,
  trackActionUnplannedDuringComplete,
} from '@/utils/tracking';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FlexProps,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { endOfDay, startOfDay } from 'date-fns';
import { isUndefined } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ICON_PADDING = rem(2.5);

type ActionRowProp = {
  idx?: number;
  action: Action;
  readOnly?: boolean;
  showIndex?: boolean;
  progressStatusButtonDisabled?: boolean;
  disableRecurringStatusChanges?: boolean;
  hideButtons?: boolean;
  onClick?: () => void;
  onDeleteAction?: (action: Action) => void;
  onUpdateBlock?: (blockId: string) => void;
  onClickCreateBlock?: (actionId: string) => void;
  onClickRemoveFromBlock?: (actionId: string) => void;
  markBlockAsComplete?: boolean;
  withScheduleButton?: boolean;
  isCompleteMode?: boolean;
  isDeleteMode?: boolean;
  isSelected?: boolean;
  onToggle?: () => void;
  recurringWeekContext?: RecurringWeekContext | null;
} & FlexProps;

function ActionRow({
  idx,
  action,
  showIndex = true,
  progressStatusButtonDisabled = false,
  disableRecurringStatusChanges = false,
  hideButtons = false,
  onClick,
  onDeleteAction,
  onUpdateBlock,
  onClickCreateBlock,
  onClickRemoveFromBlock,
  markBlockAsComplete = false,
  withScheduleButton = false,
  isCompleteMode = false,
  isDeleteMode = false,
  isSelected = false,
  readOnly = isCompleteMode,
  onToggle,
  recurringWeekContext = null,
  ...rest
}: ActionRowProp) {
  const { t } = useTranslation('ActionRow');
  const toast = useToast();

  const {
    isOpen: isOpenDeleteActionModal,
    onOpen: openDeleteActionModal,
    onClose: onCloseDeleteActionModal,
  } = useDisclosure();

  const {
    isOpen: isOpenDuplicateActionModal,
    onOpen: openDuplicateActionModal,
    onClose: onCloseDuplicateActionModal,
  } = useDisclosure();

  const {
    isOpen: isOpenLeverageRequestModal,
    onOpen: openLeverageRequestModal,
    onClose: onCloseLeverageRequestModal,
  } = useDisclosure();

  const {
    isOpen: isOpenRecurringConfirmationModal,
    onOpen: openRecurringConfirmationModal,
    onClose: onCloseRecurringConfirmationModal,
  } = useDisclosure();

  const {
    isOpen: isOpenCancelRecurringModal,
    onOpen: openCancelRecurringModal,
    onClose: onCloseCancelRecurringModal,
  } = useDisclosure();

  const {
    isOpen: isOpenRecurringStatusBlockedModal,
    onOpen: openRecurringStatusBlockedModal,
    onClose: onCloseRecurringStatusBlockedModal,
  } = useDisclosure();

  const { isOpen: isDatePickerOpen, onOpen: onDatePickerOpen, onClose: onDatePickerClose } = useDisclosure();

  const isDailyPlanPage = useIsDailyPlanPage();

  const { playCompleteAudio } = useSoundEffects();

  const [isActiveScheduleButton, setIsActiveScheduleButton] = useState(false);

  // Reset the schedule button state when action changes
  useEffect(() => {
    setIsActiveScheduleButton(false);
  }, [action?.scheduledDate, action?.weeklyPlanId]);

  // date picker information for the "move" button if isInComplete mode
  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);

  const [tabIndex, setTabIndex] = useState(isDailyPlanPage ? DAY : WEEK);
  const [dateRange, setDateRange] = useState(tabIndex === WEEK);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const { data: datePickerWeeklyId, isLoading: datePickerWeeklyIdIsLoading } = useWeeklyPlanId(scheduledDate);

  const { data: todaysWeeklyPlanId } = useWeeklyPlanId(new Date());

  const { data: selectedWeekWeeklyPlanId } = useWeeklyPlanId(selectedDate);

  const isDarkMode = useIsDarkMode();
  const [, setResolveConfirmPromise] = useState<(() => (value: boolean) => void) | null>(null);

  const hasCompleteMyPlanRecurringRange = useMemo(
    () =>
      isCompleteMode &&
      validateRecurrence(action?.event?.recurrence) &&
      recurringWeekContext?.weekStart != null &&
      recurringWeekContext?.weekEnd != null,
    [isCompleteMode, action, recurringWeekContext],
  );

  const updateActionByPk = useUpdateAction({
    onSuccess: (data) => {
      if (isCompleteMode) {
        data.updateActionByPk.weeklyPlanId != action.weeklyPlanId &&
          invalidateQueries([keys.actions.all._ctx.week(action.weeklyPlanId as string).queryKey]);
        toast({
          title: t('toasts.actionUpdated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    },
  });

  const isArchived = action.project?.isArchived || action.category.isArchived || action.isCanceled;

  const resolvedActionId = action.recurringShadowOfId ?? action.id;

  const updateActionStatus = useUpdateActionStatus();

  const completeBlockStatus = useCompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id, true),
  });

  const updateEvent = useUpdateEventByPk();

  const deleteAction = useDeleteAction({
    onSuccess: () => {
      if (isUndefined(action)) {
        return;
      }

      refetchActions(action, selectedWeekWeeklyPlanId);

      if (action.promises) {
        refetchQueries([keys.people.all.queryKey, keys.actions.leveragedAndCommitted]);
      }

      if (action?.blockId) {
        updateActionStatus.mutate({
          new: null,
          old: {
            id: resolvedActionId,
            block_id: action.blockId,
          },
        });
      }

      toast({
        title: t('toasts.actionDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onCloseDeleteActionModal();
    },
  });

  const updateBlock = useMutation({
    mutationFn: () =>
      fetchData<UpdateBlockResponse>(TOGGLE_BLOCK_COMPLETE, {
        id: action?.blockId,
        isCompleted: false,
      }),

    onSuccess: () => {
      if (isUndefined(action)) {
        return;
      }

      onUpdateBlock && onUpdateBlock(String(action.blockId));
    },
  });

  // the schedule button shows "this week" or "today" and so ignores the context of the app
  // and should always take todays date or weeklyPlanId instead
  const onClickScheduleButton = useCallback(() => {
    if (!resolvedActionId) return;

    // Used to create an immediate feedback while updating
    setIsActiveScheduleButton(true);

    updateActionByPk.mutate({
      actionId: resolvedActionId,
      set: {
        scheduledDate: isDailyPlanPage ? formatDateToString(new Date()) : null,
        weeklyPlanId: todaysWeeklyPlanId,
      },
    });
  }, [resolvedActionId, updateActionByPk, todaysWeeklyPlanId, isDailyPlanPage]);

  const isActionScheduledSameDayWeek = useMemo(() => {
    if (isDailyPlanPage) {
      return action?.scheduledDate === formatDateToString(new Date());
    } else {
      return action?.weeklyPlanId === todaysWeeklyPlanId;
    }
  }, [action?.scheduledDate, action?.weeklyPlanId, todaysWeeklyPlanId, isDailyPlanPage]);

  const recurringWeeklyModel = useMemo(() => {
    if (!recurringWeekContext?.weekStart || !recurringWeekContext?.weekEnd) {
      return null;
    }
    if (!validateRecurrence(action?.event?.recurrence)) {
      return null;
    }
    return getRecurringPeriodViewModel(
      action,
      recurringWeekContext.weekStart,
      recurringWeekContext.weekEnd,
      undefined,
      recurringWeekContext.granularity ?? 'week',
    );
  }, [action, recurringWeekContext]);

  const completeModalRecurringWeeklyModel = useMemo(() => {
    if (!isCompleteMode || !validateRecurrence(action?.event?.recurrence)) {
      return null;
    }
    if (recurringWeeklyModel) {
      return recurringWeeklyModel;
    }
    const periodStart = isDailyPlanPage ? startOfDay(selectedDate) : startOfTheWeek(selectedDate);
    const periodEnd = isDailyPlanPage ? endOfDay(selectedDate) : endOfTheWeek(selectedDate);
    return getRecurringPeriodViewModel(
      action,
      periodStart,
      periodEnd,
      undefined,
      isDailyPlanPage ? 'day' : 'week',
    );
  }, [isCompleteMode, action, recurringWeeklyModel, isDailyPlanPage, selectedDate]);

  const completeModalRecurringOccurrenceBadge = useMemo(() => {
    if (!isCompleteMode || !validateRecurrence(action?.event?.recurrence)) {
      return null;
    }
    const model = completeModalRecurringWeeklyModel;
    if (!model) {
      return null;
    }
    const total = model.activeInWeekCount + model.completedInWeekCount;
    const n = capRecurringOccurrencesForDisplay(model.granularity, total);
    return n > 0 ? n : null;
  }, [isCompleteMode, action, completeModalRecurringWeeklyModel]);

  const recurringCompletedSeriesStats = useMemo(() => {
    if (recurringWeeklyModel) {
      return null;
    }
    if (!readOnly || action.progressStatus !== COMPLETE) {
      return null;
    }
    return getRecurringCompletedSeriesStats(action);
  }, [action, readOnly, recurringWeeklyModel]);

  const recurringSeriesBadgeCount = useMemo(() => {
    if (!validateRecurrence(action?.event?.recurrence)) {
      return null;
    }
    if (recurringWeeklyModel) {
      const isCompletedPresentation =
        readOnly && (action.progressStatus === COMPLETE || Boolean(action.isRecurringSeriesCompletedRow));
      if (isCompletedPresentation) {
        const n = capRecurringOccurrencesForDisplay(
          recurringWeeklyModel.granularity,
          recurringWeeklyModel.completedInWeekCount,
        );
        return n > 0 ? n : null;
      }
      const n = capRecurringOccurrencesForDisplay(
        recurringWeeklyModel.granularity,
        recurringWeeklyModel.activeInWeekCount,
      );
      return n > 0 ? n : null;
    }
    if (
      readOnly &&
      action.progressStatus === COMPLETE &&
      recurringCompletedSeriesStats &&
      recurringCompletedSeriesStats.completedCount > 0
    ) {
      return recurringCompletedSeriesStats.completedCount;
    }
    return null;
  }, [action, readOnly, recurringWeeklyModel, recurringCompletedSeriesStats]);

  const recurringCompletedPeriodList = useMemo(() => {
    if (!readOnly || !recurringWeekContext?.weekStart || !recurringWeekContext?.weekEnd) {
      return null;
    }
    if (!validateRecurrence(action?.event?.recurrence)) {
      return null;
    }
    const showCompleted =
      action.progressStatus === COMPLETE || Boolean(action.isRecurringSeriesCompletedRow);
    if (!showCompleted) {
      return null;
    }
    const dates = getRecurringCompletedInstanceStartsInPeriod(
      action,
      recurringWeekContext.weekStart,
      recurringWeekContext.weekEnd,
    );
    if (dates.length === 0) {
      return null;
    }
    return {
      dates,
      noScheduledTime: recurringWeeklyModel?.noScheduledTime ?? !action.scheduledTime,
    };
  }, [action, readOnly, recurringWeekContext, recurringWeeklyModel]);

  const recurringLastCompletedTooltip = useMemo(() => {
    if (recurringCompletedPeriodList) {
      return null;
    }
    if (recurringWeeklyModel) {
      const showCompletedClock =
        readOnly && (action.progressStatus === COMPLETE || Boolean(action.isRecurringSeriesCompletedRow));
      const seriesLastCompleted = validateRecurrence(action?.event?.recurrence)
        ? getRecurringSeriesLastCompletedOccurrenceStart(action)
        : null;
      const lastStart = seriesLastCompleted ?? recurringWeeklyModel.completedTooltipStart;
      if (!showCompletedClock || !lastStart) {
        return null;
      }
      const usePlainCompleted =
        recurringWeeklyModel.granularity === 'day' || recurringWeeklyModel.periodKind === 'future';
      return {
        lastStart,
        noScheduledTime: recurringWeeklyModel.noScheduledTime,
        lineFormat: usePlainCompleted ? ('weeklyPlain' as const) : ('weeklyLast' as const),
      };
    }
    if (!recurringCompletedSeriesStats) {
      return null;
    }
    return {
      lastStart: recurringCompletedSeriesStats.lastCompletedStart,
      noScheduledTime: recurringCompletedSeriesStats.noScheduledTime,
      lineFormat: 'default' as const,
    };
  }, [
    action,
    readOnly,
    recurringWeeklyModel,
    recurringCompletedSeriesStats,
    recurringCompletedPeriodList,
  ]);

  const recurringOccurrenceTooltip = useMemo(() => {
    if (!validateRecurrence(action?.event?.recurrence)) {
      return null;
    }
    if (action.isRecurringSeriesCompletedRow) {
      return null;
    }
    if (readOnly && action.progressStatus === COMPLETE) {
      return null;
    }
    if (recurringWeeklyModel) {
      if (recurringWeeklyModel.activeInWeekCount > 0 && recurringWeeklyModel.activeTooltipStart) {
        const usePlainActive =
          recurringWeeklyModel.granularity === 'day' || recurringWeeklyModel.periodKind === 'past';
        return {
          nextStart: recurringWeeklyModel.activeTooltipStart,
          completedCount: recurringWeeklyModel.activeInWeekCount,
          noScheduledTime: recurringWeeklyModel.noScheduledTime,
          lineFormat: usePlainActive ? ('weeklyPlain' as const) : ('weeklyNext' as const),
        };
      }
    }
    return getRecurringOccurrenceTooltipInfo(action);
  }, [action, readOnly, recurringWeeklyModel]);

  const onCompleteBlock = useCallback(
    (blockId: string) => {
      completeBlockStatus.mutate({
        id: blockId,
      });
    },
    [completeBlockStatus],
  );

  const readOnlyNonInteractiveRow = useMemo(
    () =>
      readOnly &&
      (action.progressStatus === COMPLETE || Boolean(action.isRecurringSeriesCompletedRow)),
    [readOnly, action.progressStatus, action.isRecurringSeriesCompletedRow],
  );

  const showDialogModal = (): Promise<boolean> => {
    openRecurringConfirmationModal();
    return new Promise((resolve) => {
      setResolveConfirmPromise(() => () => resolve);
    });
  };

  const onClickProgressStatus = useCallback(
    async (progressStatus: ProgressStatus) => {
      if (
        disableRecurringStatusChanges &&
        validateRecurrence(action?.event?.recurrence) &&
        !hasCompleteMyPlanRecurringRange
      ) {
        openRecurringStatusBlockedModal();
        return;
      }

      if (hasCompleteMyPlanRecurringRange) {
        if (progressStatus !== COMPLETE) {
          openRecurringStatusBlockedModal();
          return;
        }
        const merged = mergeRecurrenceCompletingAllInstancesInRange(
          action,
          recurringWeekContext!.weekStart,
          recurringWeekContext!.weekEnd,
        );
        if (merged && action.event?.id) {
          updateEvent.mutate({
            ...action.event,
            recurrence: merged,
            start: action.event.start,
            id: action.event.id,
            showSyncEvent: false,
          });
          playCompleteAudio();
          if (markBlockAsComplete && action.blockId) {
            onCompleteBlock(action.blockId);
          }
        }
        trackActionProgress(progressStatus, action);
        return;
      }

      if (validateRecurrence(action?.event?.recurrence)) {
        const recurrence = structuredClone(action?.event?.recurrence);
        const confirmedOption = await showDialogModal();
        if (!confirmedOption) {
          return;
        }
        if (recurrence?.exceptions?.custom) {
          Object.values(recurrence.exceptions.custom).forEach((exception) => {
            delete exception.progressStatus;
          });
        }

        updateEvent.mutate({
          ...action?.event,
          recurrence,
          start: action?.event?.start,
          id: action?.event?.id || '',
          showSyncEvent: false,
          allowCompletionRemoval: true,
        });
      }

      if (action?.block?.isCompleted && progressStatus !== 'complete') {
        updateBlock.mutate();
      }

      if (!resolvedActionId) return;

      updateActionByPk.mutate({
        actionId: resolvedActionId,
        set: {
          progressStatus,
        },
        showSyncEvent: false,
      });

      if (progressStatus === 'complete') {
        playCompleteAudio();
      }

      if (markBlockAsComplete && progressStatus === 'complete' && action.blockId) {
        onCompleteBlock(action.blockId);
      }

      trackActionProgress(progressStatus, action);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      action,
      disableRecurringStatusChanges,
      hasCompleteMyPlanRecurringRange,
      recurringWeekContext,
      openRecurringStatusBlockedModal,
      openRecurringConfirmationModal,
      updateActionByPk,
      updateBlock,
      markBlockAsComplete,
      onCompleteBlock,
      playCompleteAudio,
      updateEvent,
    ],
  );

  const onClickPreferredButton = useCallback(async () => {
    if (!resolvedActionId) return;

    if (validateRecurrence(action?.event?.recurrence)) {
      const recurrence = structuredClone(action?.event?.recurrence);
      const confirmedOption = await showDialogModal();
      if (!confirmedOption) {
        return;
      }
      if (recurrence?.exceptions?.custom) {
        Object.values(recurrence.exceptions.custom).forEach((exception) => {
          delete exception.dateOfStarring;
        });
      }

      updateEvent.mutate({
        ...action?.event,
        recurrence,
        start: action?.event?.start,
        id: action?.event?.id || '',
        showSyncEvent: false,
      });
    }

    if (!action?.dateOfStarring) {
      // action is about to be starred in the below mutation
      trackActionStarred();
    }

    updateActionByPk.mutate({
      actionId: resolvedActionId,
      set: {
        dateOfStarring: typeof action?.dateOfStarring === 'string' ? null : new Date(),
      },
      showSyncEvent: false,
    });
  }, [action?.dateOfStarring, resolvedActionId, updateActionByPk]);

  const onHandleDeleteAction = useCallback(() => {
    if (!resolvedActionId) return;

    deleteAction.mutate({ actionId: resolvedActionId });
  }, [deleteAction, resolvedActionId]);

  const handleClickCreateBlock = useCallback(() => {
    if (!resolvedActionId || !onClickCreateBlock) {
      return undefined;
    }

    onClickCreateBlock(resolvedActionId);
  }, [resolvedActionId, onClickCreateBlock]);

  const handleClickRemoveFromBlock = useCallback(() => {
    if (!resolvedActionId || !onClickRemoveFromBlock) {
      return undefined;
    }

    onClickRemoveFromBlock(resolvedActionId);
  }, [resolvedActionId, onClickRemoveFromBlock]);

  const onHandleUnplan = useCallback(
    (action: Action) => {
      void action;
      updateActionByPk.mutate({
        actionId: resolvedActionId,
        set: {
          weeklyPlanId: null,
          scheduledDate: null,
          scheduledTime: null,
        },
      });

      isCompleteMode && trackActionUnplannedDuringComplete();
    },
    [updateActionByPk, isCompleteMode, resolvedActionId],
  );

  const onChangeTabs = useCallback((index: string | number) => {
    if ((index as DayWeek) === WEEK) {
      setScheduledTime(null);
    }

    setTabIndex(index as DayWeek);
    setDateRange((index as DayWeek) === WEEK);
  }, []);

  const handleScheduledDate = useCallback(
    (date: Date) => {
      if (scheduledTime) {
        date.setHours(scheduledTime.getHours());
        date.setMinutes(scheduledTime.getMinutes());

        setScheduledTime(date);
      }

      setScheduledDate(date);
    },
    [scheduledTime],
  );

  const handleScheduledTime = useCallback(
    (date: Date | null) => {
      if (!date) {
        setScheduledTime(null);
        return;
      }

      if (scheduledDate) {
        scheduledDate.setHours(date.getHours());
        scheduledDate.setMinutes(date.getMinutes());
      }

      setScheduledTime(scheduledDate);
    },
    [scheduledDate],
  );

  const onMoveToAnotherDay = useCallback(
    (action: Action) => {
      onDatePickerClose();

      if (action.progressStatus === 'complete') return;

      const moveTargetId = action.recurringShadowOfId ?? action.id;

      updateActionByPk.mutate({
        actionId: moveTargetId,
        set: {
          weeklyPlanId: datePickerWeeklyId,
          scheduledDate: tabIndex === WEEK ? null : scheduledDate ? formatDateToString(scheduledDate) : null,
          // always setting scheduledTime to null for now as while I was working on this
          // I found a bug whereby the time that was passed into this was actually never set and the functionality
          // does not work. Setting scheduledTime: scheduledTime also does not work properly as when this function is called
          // the scheduledTime is has not yet updated, therefore it's incorrect.
          // It's been agreed to work on this properly in a future ticket, and completely remove the set time
          // functionality for now (since it was always broken)
          scheduledTime: null,
          timezone: '',
          // scheduledTime: tabIndex === WEEK ? null : scheduledTime ? localDateToUTC(scheduledTime).scheduledTime : null,
          // timezone: scheduledTime ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
        },
      });

      isCompleteMode && trackActionMovedDuringComplete();
    },
    [onDatePickerClose, updateActionByPk, scheduledDate, isCompleteMode, datePickerWeeklyId, tabIndex],
  );

  const onHandleComplete = useCallback(
    (actionArg: Action) => {
      const hasRange =
        isCompleteMode &&
        validateRecurrence(actionArg?.event?.recurrence) &&
        recurringWeekContext?.weekStart != null &&
        recurringWeekContext?.weekEnd != null;

      if (disableRecurringStatusChanges && validateRecurrence(actionArg?.event?.recurrence) && !hasRange) {
        openRecurringStatusBlockedModal();
        return;
      }

      if (hasRange) {
        const merged = mergeRecurrenceCompletingAllInstancesInRange(
          actionArg,
          recurringWeekContext!.weekStart,
          recurringWeekContext!.weekEnd,
        );
        if (merged && actionArg.event?.id) {
          updateEvent.mutate({
            ...actionArg.event,
            recurrence: merged,
            start: actionArg.event.start,
            id: actionArg.event.id,
            showSyncEvent: false,
          });
          playCompleteAudio();
          if (markBlockAsComplete && actionArg.blockId && onCompleteBlock) {
            onCompleteBlock(actionArg.blockId);
          }
        }
        return;
      }

      const completeTargetId = actionArg.recurringShadowOfId ?? actionArg.id;

      updateActionByPk.mutate({
        actionId: completeTargetId,
        set: {
          progressStatus: 'complete',
        },
        showSyncEvent: false,
      });

      if (markBlockAsComplete && actionArg.blockId && onCompleteBlock) {
        onCompleteBlock(actionArg.blockId);
      }
    },
    [
      disableRecurringStatusChanges,
      isCompleteMode,
      recurringWeekContext,
      openRecurringStatusBlockedModal,
      updateActionByPk,
      updateEvent,
      onCompleteBlock,
      markBlockAsComplete,
      playCompleteAudio,
    ],
  );

  const handleArchiveToggle = useCallback(
    (canceled: boolean) => {
      if (!resolvedActionId) return;
      updateActionByPk.mutate({
        actionId: resolvedActionId,
        set: { isCanceled: canceled },
      });
    },
    [resolvedActionId, updateActionByPk],
  );

  const handleCancelAction = useCallback(() => {
    if (validateRecurrence(action?.event?.recurrence)) {
      openCancelRecurringModal();
      return;
    }
    handleArchiveToggle(true);
  }, [action?.event?.recurrence, handleArchiveToggle, openCancelRecurringModal]);

  const handleConfirmCancelRecurring = useCallback(() => {
    handleArchiveToggle(true);
    onCloseCancelRecurringModal();
  }, [handleArchiveToggle, onCloseCancelRecurringModal]);

  if (action.isCanceled) {
    return (
      <Flex
        sx={{
          '@media print': {
            border: `${rem(1)} solid var(--chakra-colors-print-stroke)`,
            overflow: 'hidden !important',
          },
        }}
        alignItems="center"
        gap={rem(10)}
        width="full"
        height={rem(32)}
        padding={`${rem(6)} ${rem(7)}`}
        background={action.dateOfStarring ? generateLinearGradient(action.category.color) : 'inherit'}
        opacity={0.6}
        borderRadius="4"
        cursor="not-allowed"
        backgroundColor="background-tertiary"
        css={{
          touchAction: 'none',
        }}
        {...rest}
      >
        <IconProgressState
          state={action.progressStatus}
          onClick={undefined}
          disabled={true}
          tabIndex={-1}
          dimension={rem(14)}
          isDisabled={true}
        />
        {showIndex && (
          <Text as="span" textStyle="actionRowIndex">
            {idx}
          </Text>
        )}
        <EllipsisTooltipText flex={1} textStyle="actionRowTitle" color="text-primary" textAlign="left">
          {action.title}
        </EllipsisTooltipText>
        <Button
          width={rem(90)}
          height={rem(24)}
          minHeight={rem(24)}
          background="background-primary"
          borderColor={'stroke-primary'}
          borderRadius={rem(6)}
          _hover={{
            backgroundColor: 'background-secondary',
            cursor: 'pointer',
          }}
          _disabled={{
            _hover: {
              backgroundColor: 'background-secondary',
              cursor: 'default',
            },
          }}
          backgroundColor={'background-primary'}
          isDisabled={action.project?.isArchived || action.category.isArchived}
          onClick={() => handleArchiveToggle(false)}
          paddingX="0"
          variant="secondary"
        >
          <Text textStyle="small" color="text-primary">
            {t('buttons.restore')}
          </Text>
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        sx={{
          '@media print': {
            border: `${rem(1)} solid var(--chakra-colors-print-stroke)`,
            overflow: 'hidden !important',
          },
        }}
        alignItems="center"
        gap={rem(10)}
        width="full"
        height={rem(32)}
        padding={`${rem(6)} ${rem(7)}`}
        background={action.dateOfStarring ? generateLinearGradient(action.category.color) : 'inherit'}
        opacity={action.progressStatus === COMPLETE || isArchived ? '0.6' : '1'}
        borderRadius="4"
        cursor={readOnlyNonInteractiveRow || isArchived ? 'not-allowed' : 'inherit'}
        backgroundColor="background-tertiary"
        css={{
          touchAction: 'none',
        }}
        onClick={isArchived || readOnlyNonInteractiveRow ? undefined : onClick}
        {...rest}
      >
        <IconProgressState
          state={action.progressStatus}
          onClick={onClickProgressStatus}
          disabled={progressStatusButtonDisabled}
          tabIndex={readOnly || isArchived ? -1 : 0}
          dimension={rem(14)}
          preUpdate={false}
          isDisabled={isArchived}
          blockCompletion={hasCompleteMyPlanRecurringRange}
        />

        {showIndex && (
          <Text as="span" textStyle="actionRowIndex">
            {idx}
          </Text>
        )}

        <EllipsisTooltipText flex={1} textStyle="actionRowTitle" color="text-primary" textAlign="left">
          {action.title}
        </EllipsisTooltipText>

        {isDeleteMode && validateRecurrence(action?.event?.recurrence) && (
          <Flex alignItems="center" flexShrink={0} onClick={(e) => e.stopPropagation()}>
            <Tooltip
              aria-label={t('ariaLabels.recurrence')}
              label={capitalizeFirstLetter(action.event?.recurrence?.rules?.[0]?.frequency ?? '')}
              placement="top"
            >
              <Flex>
                <Icon as={IconRecurrence} boxSize={rem(14)} color="text-primary" />
              </Flex>
            </Tooltip>
          </Flex>
        )}

        {isCompleteMode && validateRecurrence(action?.event?.recurrence) && (
          <Flex
            alignItems="center"
            flexShrink={0}
            gap={rem(4)}
            onClick={(e) => e.stopPropagation()}
          >
            {completeModalRecurringOccurrenceBadge != null && (
              // eslint-disable-next-line chakra-ui/props-order
              <Box as="span" display="inline-block" flexShrink={0} textStyle="small" color="text-primary" fontWeight={700}>
                {completeModalRecurringOccurrenceBadge}x
              </Box>
            )}
            <Tooltip
              aria-label={t('ariaLabels.recurrence')}
              label={capitalizeFirstLetter(action.event?.recurrence?.rules?.[0]?.frequency ?? '')}
              placement="top"
            >
              <Flex>
                <Icon as={IconRecurrence} boxSize={rem(14)} color="text-primary" />
              </Flex>
            </Tooltip>
          </Flex>
        )}

        {!hideButtons && !isDeleteMode && (
          <Flex alignItems="center" gap={rem(8)} cursor="default" onClick={(e) => e.stopPropagation()}>
            {!isCompleteMode && (
              <>
                {action?.project?.name && (
                  <Tooltip
                    aria-label={t('tooltips.project', { name: action.project.name })}
                    label={t('tooltips.project', { name: action.project.name })}
                    placement="top"
                  >
                    <Flex>
                      <Icon as={IconProject} boxSize={rem(14)} />
                    </Flex>
                  </Tooltip>
                )}

                {action?.promises?.[0]?.kind === ActionRelation.Leverage && (
                  <>
                    <Box
                      onClick={
                        action.progressStatus === COMPLETE
                          ? undefined
                          : () => {
                              action?.promises?.[0]?.leverageRequestStatus ? openLeverageRequestModal() : null;
                            }
                      }
                    >
                      <PersonActionPreview promises={action?.promises} />
                    </Box>
                    <LeverageRequestModal
                      promise={action.promises[0]}
                      title={action.title}
                      isOpen={isOpenLeverageRequestModal}
                      onClose={onCloseLeverageRequestModal}
                    />
                  </>
                )}

                {action?.promises?.[0]?.kind === ActionRelation.Commit && (
                  <PersonActionPreview promises={action?.promises} />
                )}

                {action?.event?.recurrence?.rules && (
                  <Flex alignItems="center" gap={rem(4)}>
                    {recurringSeriesBadgeCount != null && recurringSeriesBadgeCount > 0 && (
                      // eslint-disable-next-line chakra-ui/props-order
                      <Box as="span" display="inline-block" flexShrink={0} textStyle="small" color="text-primary" fontWeight={700}>
                        {recurringSeriesBadgeCount}x
                      </Box>
                    )}
                    <Tooltip
                      aria-label={t('ariaLabels.recurrence')}
                      label={capitalizeFirstLetter(action.event.recurrence?.rules?.[0]?.frequency)}
                      placement="top"
                    >
                      <Flex>
                        <Icon as={IconRecurrence} boxSize={rem(14)} color="text-primary" />
                      </Flex>
                    </Tooltip>
                  </Flex>
                )}

                <Clock
                  duration={action.duration || '00:00:00'}
                  scheduledDate={action.scheduledDate}
                  scheduledTime={action.scheduledTime}
                  timeZone={action.timezone}
                  gmtOffset={action.gmtOffset}
                  iconColor="text-primary"
                  recurringLastCompletedTooltip={recurringLastCompletedTooltip}
                  recurringCompletedPeriodList={recurringCompletedPeriodList}
                  recurringOccurrenceTooltip={recurringOccurrenceTooltip}
                />
              </>
            )}

            {(!isCompleteMode || (isCompleteMode && !!action.dateOfStarring)) && (
              <PreferredButton
                isStarred={!!action.dateOfStarring}
                onClick={
                  readOnly || isArchived
                    ? undefined
                    : action.progressStatus === COMPLETE
                      ? undefined
                      : onClickPreferredButton
                }
                cursor={readOnly || isArchived ? 'default' : 'pointer'}
                iconColor={isDarkMode ? 'white.500' : 'gray.500'}
                padding={ICON_PADDING}
                readOnly={readOnly || isArchived}
              />
            )}

            {withScheduleButton && (
              <ScheduleButton
                onClick={!isActionScheduledSameDayWeek && !isArchived ? onClickScheduleButton : undefined}
                isActive={isActiveScheduleButton || isActionScheduledSameDayWeek}
                marginRight={ICON_PADDING}
              />
            )}

            {!isArchived && !readOnly && !isCompleteMode && (
              <ThreeDotsActionButton
                iconColor="text-primary"
                padding={ICON_PADDING}
                marginX={`-${ICON_PADDING}`}
                onDuplicateAction={action.scheduledDate ? openDuplicateActionModal : undefined}
                onEditAction={onClick}
                onRemoveActionItemClick={openDeleteActionModal}
                onCreateBlock={
                  onClickCreateBlock && !validateRecurrence(action?.event?.recurrence)
                    ? handleClickCreateBlock
                    : undefined
                }
                onRemoveFromBlock={onClickRemoveFromBlock ? handleClickRemoveFromBlock : undefined}
                onCancelAction={!action.isCanceled ? handleCancelAction : undefined}
              />
            )}

            {isCompleteMode && (
              <>
                {!validateRecurrence(action?.event?.recurrence) && (
                  <>
                    <Button onClick={() => onHandleUnplan(action)} variant="text">
                      {t('buttons.unplan')}
                    </Button>

                    <Popover
                      isOpen={isDatePickerOpen}
                      onClose={onDatePickerClose}
                      onOpen={onDatePickerOpen}
                      placement="right"
                    >
                      <PopoverTrigger>
                        <Button aria-label={t('tooltips.selectDateRange')} variant="text">
                          {t('buttons.move')}
                        </Button>
                      </PopoverTrigger>

                      {/* !TODO find a better approach, regarding fixing the z-index of the popover
                  inside the portal. we need the portal to make sure the popover is shown over app content
                  but the popover and modal zindex conflict with each other so inside a modal it displays incorrectly
                  UNDER the modal... there is alternative solutions that affect the popover globally
                  but at this point it's too much of a risk before production deploy 
                  https://github.com/chakra-ui/chakra-ui/issues/6794
                  */}
                      <Portal>
                        <Box
                          sx={{
                            '& .chakra-popover__popper': {
                              zIndex: 'popover',
                            },
                          }}
                        >
                          <PopoverContent
                            width={{ lg: rem(416) }}
                            padding={{ base: rem(8), lg: rem(16) }}
                            border={`${rem(1)} solid`}
                            borderColor="stroke-primary"
                            borderRadius={rem(4)}
                            backgroundColor="background-primary"
                          >
                            <PopoverBody className={tabIndex === WEEK ? 'week-style' : 'day-style'} padding={0}>
                              <RPMDateTimePicker
                                dateRange={dateRange}
                                onChangeTabs={onChangeTabs}
                                onSave={() => onMoveToAnotherDay(action)}
                                onCancel={onDatePickerClose}
                                scheduledTime={scheduledTime}
                                selectedDate={scheduledDate ?? new Date()}
                                tabIndex={tabIndex}
                                updateScheduledTime={handleScheduledTime}
                                updateSelectedDate={handleScheduledDate}
                                // disabling time for now
                                withTimePicker={false}
                                isLoading={!datePickerWeeklyId || datePickerWeeklyIdIsLoading}
                              />
                            </PopoverBody>
                          </PopoverContent>
                        </Box>
                      </Portal>
                    </Popover>
                  </>
                )}

                <Button
                  borderColor="var(--chakra-colors-green-400)"
                  onClick={() => onHandleComplete(action)}
                  variant="text"
                >
                  {t('buttons.complete')}
                </Button>
              </>
            )}
          </Flex>
        )}
        {hideButtons && !!action.dateOfStarring && (
          <PreferredButton
            isStarred={!!action.dateOfStarring}
            iconColor={action.dateOfStarring ? action.category.color : 'text-primary'}
            cursor="default"
            padding={ICON_PADDING}
            readOnly={true}
          />
        )}

        {isDeleteMode && <Checkbox isChecked={isSelected} onChange={() => onToggle?.()} />}
      </Flex>

      {isOpenDeleteActionModal && (
        <DeleteActionModal
          isOpen={isOpenDeleteActionModal}
          onCloseDeleteActionModal={onCloseDeleteActionModal}
          onDeleteAction={onHandleDeleteAction}
          hasFrequency={validateRecurrence(action?.event?.recurrence)}
        />
      )}

      {isOpenDuplicateActionModal && (
        <DuplicateActionModal
          action={action}
          isOpen={isOpenDuplicateActionModal}
          onCloseDuplicateActionModal={onCloseDuplicateActionModal}
        />
      )}

      {isOpenRecurringConfirmationModal && (
        <RecurringConfirmationModal
          isOpen={isOpenRecurringConfirmationModal}
          onCloseConfirmationModal={onCloseRecurringConfirmationModal}
          setResolveConfirmPromise={setResolveConfirmPromise}
        />
      )}

      {isOpenCancelRecurringModal && (
        <CancelRecurringActionModal
          isOpen={isOpenCancelRecurringModal}
          onClose={onCloseCancelRecurringModal}
          onConfirm={handleConfirmCancelRecurring}
        />
      )}

      <RecurringStatusBlockedModal
        isOpen={isOpenRecurringStatusBlockedModal}
        onClose={onCloseRecurringStatusBlockedModal}
        title={t('recurringStatusBlocked.title')}
        message={t('recurringStatusBlocked.message')}
        okLabel={t('recurringStatusBlocked.ok')}
      />
    </>
  );
}

export default memo(ActionRow);
